import { queryKey } from 'shared/src/constant';
import { memo, Suspense, useEffect, useState } from 'react';
import { AnalyticsScreen } from 'shared/src/context';
import { useGetRecentlyViewedGoods } from './hooks/useGetRecentlyViewedGoods';
import { ActivityIndicator } from 'react-native';
import {
  bridge,
  getNumberRange,
  isAndroid,
  useNavigator,
} from 'shared/src/utils';
import { Header } from './component/Header';
import { useLogViewEvent } from './hooks/useLogViewEvent';
import {
  Flex,
  GoodsCard3Column,
  StackScreen,
  VerticalSpacing,
  useSafeArea,
  useScreenSize,
  type GoodsData,
} from '@banhala/ui-kit';
import { JotaiProvider } from './jotai/JotaiProvider';
import {
  type InfiniteRecentlyViewedGoods,
  SyncZZimData,
} from './jotai/infiniteRecentlyViewedGood.atom';
import {
  type QueryObserverResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { likeGoodsService } from 'shared/src/api';
import { useAddLikeGoods } from 'shared/src/hooks';
import { SearchIcon, CartIcon } from 'shared/src/component';

import { EmptyListView } from './component/EmptyListView';
import { useReportTTFD } from './hooks/useReportTTFD';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useHandleBackPressed } from 'shared/src/hooks';

const RecentlyViewedGoods = () => {
  const { width } = useScreenSize();
  const { bottom } = useSafeArea();
  useLogViewEvent();
  const { showBackButton, onPressBack } = useHandleBackPressed();

  return (
    <AnalyticsScreen screen="RECENTLY_VIEWED_GOODS">
      <StackScreen
        title="최근 본 상품"
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[
          <SearchIcon />,
          <CartIcon
            onPressCart={() => {
              bridge.logAnalyticsEvent({
                channel: ['firebase', 'facebook', 'amplitude', 'ablylog'],
                name: 'CLICK_CART',
                analytics: {
                  SCREEN: 'RECENTLY_VIEWED_GOODS',
                  LANDING_FROM: 'CARTICON',
                },
              });
            }}
          />,
        ]}
      >
        <Suspense
          fallback={
            <Flex flex={1} width={width} margin={{ horizontal: 'auto' }}>
              <Header />
              <Flex.Center flex={1}>
                <ActivityIndicator />
              </Flex.Center>
            </Flex>
          }
        >
          <ContainerList />
          {isAndroid && <Flex height={bottom} />}
        </Suspense>
      </StackScreen>
    </AnalyticsScreen>
  );
};

const ContainerList = memo(() => {
  const {
    recentlyViewedGoods,
    getNextRecentlyViewedGoods,
    refetch: refreshRecentlyViewedGoods,
    isRefetching: isRefreshingRecentlyViewedGoods,
    hasNextPage,
  } = useGetRecentlyViewedGoods();

  if (recentlyViewedGoods.goods.length === 0) {
    return <EmptyListView />;
  }

  return (
    <JotaiProvider infiniteRecentlyViewedGoods={recentlyViewedGoods}>
      <SyncZZimData>
        {({ syncedInfiniteRecentlyViewedGoods }) => (
          <GoodsListView
            syncedInfiniteRecentlyViewedGoods={
              syncedInfiniteRecentlyViewedGoods
            }
            hasNextRecentlyViewedGoods={hasNextPage}
            getNextRecentlyViewedGoods={getNextRecentlyViewedGoods}
            refreshRecentlyViewedGoods={refreshRecentlyViewedGoods}
            isRefreshingRecentlyViewedGoods={isRefreshingRecentlyViewedGoods}
          />
        )}
      </SyncZZimData>
    </JotaiProvider>
  );
});

const GoodsListView = (props: {
  syncedInfiniteRecentlyViewedGoods: InfiniteRecentlyViewedGoods;
  getNextRecentlyViewedGoods: () => void;
  hasNextRecentlyViewedGoods: boolean;
  refreshRecentlyViewedGoods: () => Promise<QueryObserverResult>;
  isRefreshingRecentlyViewedGoods: boolean;
}) => {
  useReportTTFD();
  const { navigator } = useNavigator();
  const queryClient = useQueryClient();

  const { mutate: mutateRemoveLikeGoods } = useMutation({
    mutationFn: likeGoodsService.delete,
    onSuccess: (_, { sno }) => {
      queryClient.invalidateQueries({
        queryKey: queryKey.getInfiniteLikeFolders,
      });
      bridge.likeGoods({
        goods_sno: sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [sno]: false }));
    },
  });
  const [isOnRefresh, setIsOnRefresh] = useState<boolean>(false);

  const [likeMap, setLikeMap] = useState<{ [key: number]: boolean }>(() =>
    props.syncedInfiniteRecentlyViewedGoods.goods.reduce((acc, val) => {
      return {
        ...acc,
        [val.sno]: val.like,
      };
    }, {}),
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'RECENTLY_VIEWED_GOODS',
  });

  useEffect(() => {
    const syncLikeMap = props.syncedInfiniteRecentlyViewedGoods.goods.reduce(
      (acc, val) => {
        return {
          ...acc,
          [val.sno]: val.like,
        };
      },
      {},
    );
    setLikeMap(syncLikeMap);
  }, [props.syncedInfiniteRecentlyViewedGoods]);
  const { logDisplayGoods } = useLogDisplayGoods();
  return (
    <GoodsCard3Column<InfiniteRecentlyViewedGoods['goods'][number]>
      list={props.syncedInfiniteRecentlyViewedGoods.goods}
      onPressItem={(item, index) => {
        logOnPressGoods({
          index,
          item,
          list_id: props.syncedInfiniteRecentlyViewedGoods.list_id,
          list_params: props.syncedInfiniteRecentlyViewedGoods.list_params,
        });
        navigator.moveToGoods({
          sno: item.sno,
          firstPageRendering: item.first_page_rendering,
        });
      }}
      onPressLike={async (item, index) => {
        logOnPressGoodsLike({
          item,
          index,
          list_id: props.syncedInfiniteRecentlyViewedGoods.list_id,
          list_params: props.syncedInfiniteRecentlyViewedGoods.list_params,
          likeMap,
        });

        if (!likeMap[item.sno]) {
          addLikeGoods(item.sno, {
            SCREEN: 'RECENTLY_VIEWED_GOODS',
          });
          return;
        }
        await mutateRemoveLikeGoods({
          list_params_referrer:
            props.syncedInfiniteRecentlyViewedGoods.list_params,
          sno: item.sno,
        });
      }}
      onEndReachedThreshold={1}
      keyExtractor={(_item, index) => index.toString()}
      ListFooterComponent={() => <VerticalSpacing size={8} />}
      onViewableItemsChanged={logDisplayGoods}
      likeMap={likeMap}
      ListHeaderComponent={Header}
      onEndReached={props.getNextRecentlyViewedGoods}
      onRefresh={async () => {
        setIsOnRefresh(true);
        props.refreshRecentlyViewedGoods().finally(() => setIsOnRefresh(false));
      }}
      refreshing={isOnRefresh && props.isRefreshingRecentlyViewedGoods}
    />
  );
};

export default RecentlyViewedGoods;

const logOnPressGoods = ({
  item,
  list_id,
  list_params,
  index,
}: {
  item: GoodsData & InfiniteRecentlyViewedGoods['goods'][number];
  list_id: string;
  list_params: string;
  index: number;
}) => {
  bridge.logAnalyticsEvent({
    channel: ['firebase', 'facebook', 'amplitude', 'ablylog'],
    name: 'CLICK_GOODS',
    analytics: {
      SCREEN: 'RECENTLY_VIEWED_GOODS',
      CATEGORY_DEPTH: item.category?.depth,
      CATEGORY_NAME: item.category?.name,
      CATEGORY_SNO: item.category?.sno.toString(),
      DELIVERY_TYPE: item.delivery_type,
      DISCOUNT_RATE: item.discount_rate,
      GOODS_NAME: item.name,
      GOODS_SNO: item.sno.toString(),
      IMAGE_BADGE_LIST: item.render.data.image_badge_list.map(
        ({ type }) => type,
      ),
      IS_AD: item.ad !== null ? '1' : '0',
      IS_NEW: item.is_new ? '1' : '0',
      // item.likes_count가 있으면 item.likes_count를 사용해야한다.
      // 해당 페이지는 item.likes_count가 없어서 null로 처리
      LIKES_COUNT: null,
      // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
      // 해당 페이지는 item.likes_count가 없어서 null로 처리
      LIKES_COUNT_RANGE: null,
      LIST_ID: list_id,
      LIST_INDEX: index,
      LIST_PARAMS: list_params,
      MARKET_NAME: item.market.name,
      MARKET_SNO: item.market.sno.toString(),
      MARKET_TYPE: item.market.market_type.name,
      ORIGINAL_PRICE:
        item.first_page_rendering?.original_price ??
        item.linked_option.original_price,
      REVIEW_COUNT: item.total_review_count,
      REVIEW_COUNT_RANGE: getNumberRange({
        target: item.total_review_count,
        rangeUnit: 50,
      }),
      REVIEW_RATING: item.positive_review_rate,
      SALES_PRICE: item.price,
      SALES_PRICE_RANGE: getNumberRange({
        target: item.price,
        rangeUnit: 5000,
      }),
      STANDARD_CATEGORY_NAME: item.standard_category.name,
      STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
      MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
      INDEX: index,
    },
  });
};
const logOnPressGoodsLike = ({
  item,
  index,
  list_id,
  list_params,
  likeMap,
}: {
  item: GoodsData & InfiniteRecentlyViewedGoods['goods'][number];
  list_id: string;
  list_params: string;
  index: number;
  likeMap: { [key: number]: boolean };
}) => {
  const analytics = {
    SCREEN: 'RECENTLY_VIEWED_GOODS',
    CATEGORY_DEPTH: item.category?.depth,
    CATEGORY_NAME: item.category?.name,
    CATEGORY_SNO: item.category?.sno.toString(),
    DELIVERY_TYPE: item.delivery_type,
    DISCOUNT_RATE: item.discount_rate,
    GOODS_NAME: item.name,
    GOODS_SNO: item.sno.toString(),
    IMAGE_BADGE_LIST: item.render.data.image_badge_list.map(({ type }) => type),
    IS_AD: item.ad !== null ? '1' : '0',
    IS_NEW: item.is_new ? '1' : '0',
    // item.likes_count가 있으면 item.likes_count를 사용해야한다.
    // 해당 페이지는 item.likes_count가 없어서 null로 처리
    LIKES_COUNT: null,
    // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
    // 해당 페이지는 item.likes_count가 없어서 null로 처리
    LIKES_COUNT_RANGE: null,
    LIST_ID: list_id,
    LIST_INDEX: index,
    LIST_PARAMS: list_params,
    MARKET_NAME: item.market.name,
    MARKET_SNO: item.market.sno.toString(),
    MARKET_TYPE: item.market.market_type.name,
    ORIGINAL_PRICE:
      item.first_page_rendering?.original_price ??
      item.linked_option.original_price,
    REVIEW_COUNT: item.total_review_count,
    REVIEW_COUNT_RANGE: getNumberRange({
      target: item.total_review_count,
      rangeUnit: 50,
    }),
    REVIEW_RATING: item.positive_review_rate,
    SALES_PRICE: item.price,
    SALES_PRICE_RANGE: getNumberRange({
      target: item.price,
      rangeUnit: 5000,
    }),
    STANDARD_CATEGORY_NAME: item.standard_category.name,
    STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
    MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
    INDEX: index,
  };
  if (!likeMap[item.sno]) {
    bridge.logAnalyticsEvent({
      channel: ['firebase', 'facebook', 'amplitude', 'ablylog'],
      name: 'CLICK_LIKE_GOODS',
      analytics,
    });
    return;
  }
  bridge.logAnalyticsEvent({
    channel: ['firebase', 'facebook', 'amplitude', 'ablylog'],
    name: 'CLICK_UNLIKE_GOODS',
    analytics,
  });
};
